import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Modal, 
  Button, 
  Table, 
  Card, 
  Badge, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Row, 
  Col,
  Pagination
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { cameraAPI, departmentAPI, edgeDeviceAPI, clearCache } from '../utils/api';
import type { Camera, CreateCameraRequest, UpdateCameraRequest } from '../types/camera';
import type { Department } from '../types/department';
import type { EdgeDevice } from '../types/edgedevice';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';

// Lightweight icon replacements to reduce bundle size
const CameraIcon = () => (
  <i className="bi bi-camera-video me-1"></i>
);




const LocationIcon = () => (
  <i className="bi bi-geo-alt"></i>
);



const PersonIcon = () => (
  <i className="bi bi-person"></i>
);

const LockIcon = () => (
  <i className="bi bi-lock"></i>
);





// Add sort icons
const SortIcon = () => (
  <i className="bi bi-filter"></i>
);

const SortUpIcon = () => (
  <i className="bi bi-sort-up"></i>
);

const SortDownIcon = () => (
  <i className="bi bi-sort-down"></i>
);

// Memoized Camera Card Component for Mobile
const CameraCard = memo(({ camera, onViewDetails, onEdit, modalLoading, canEdit }: {
  camera: Camera;
  onViewDetails: (id: number) => void;
  onEdit: (camera: Camera) => void;
  modalLoading: boolean;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'cameras' });
  const { t: tc } = useTranslation('common');
  const { currentCompany } = useAuth();
  
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInCompanyTimezone(dateString, currentCompany?.timezone || 'UTC');
  }, [currentCompany?.timezone]);

  return (
    <Card className="mobile-camera-card m-3 an-data-card">
      <Card.Body className="an-data-card">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <CameraIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(camera.name)}</h6>
              <small className="text-muted">ID: {renderCellValue(camera.id)}</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {canEdit && (
              <Button
                variant="link"
                size="sm"
                className="text-primary p-1 text-decoration-none"
                onClick={() => onEdit(camera)}
                title={t('editCamera')}
              >
                <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
              </Button>
            )}
            <Badge bg={camera.status ? 'success' : 'danger'}>
              {camera.status ? tc('status.active') : tc('status.inactive')}
            </Badge>
          </div>
        </div>
        
        <Row className="g-2 mb-3">
          <Col xs={12}>
            <small className="text-muted d-block">{t('cameraNumber')}</small>
            <span className="fw-medium">{renderCellValue(camera.camera_number)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">
              <LocationIcon />
              <span className="ms-1">{t('location')}</span>
            </small>
            <span>{renderCellValue(camera.location)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{tc('labels.department')}</small>
            <span>{renderCellValue(camera.department_name)}</span>
          </Col>
          <Col xs={12}>
            <small className="text-muted d-block">{t('lastImage')}</small>
            <span>{camera.latest_image_timestamp ? formatDateTime(camera.latest_image_timestamp) : tc('messages.na')}</span>
          </Col>
        </Row>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {camera.created_at ? formatDateTime(camera.created_at) : tc('messages.na')}
          </small>
          <Button
            variant="link"
            size="sm"
            className="text-primary p-1 text-decoration-none"
            onClick={() => onViewDetails(camera.id)}
            disabled={modalLoading}
            title={t('viewDetails')}
          >
            <i className="bi bi-eye"></i> <span className="ms-1">{tc('buttons.view')}</span>
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
});

CameraCard.displayName = 'CameraCard';

// Memoized Table Row Component
const CameraTableRow = memo(({ 
  camera, 
  onViewDetails,
  onEdit, 
  modalLoading,
  companyTimezone,
  canEdit
}: { 
  camera: Camera; 
  onViewDetails: (id: number) => void;
  onEdit: (camera: Camera) => void; 
  modalLoading: boolean;
  companyTimezone: string;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'cameras' });
  const { t: tc } = useTranslation('common');
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInCompanyTimezone(dateString, companyTimezone);
  }, [companyTimezone, tc]);

  return (
    <tr>
      <td>
        <Badge bg={camera.status ? 'success' : 'danger'}>
          {camera.status ? tc('status.active') : tc('status.inactive')}
        </Badge>
      </td>
      <td>
        <div className="d-flex align-items-center">
          <CameraIcon />
          <span className="fw-medium">{renderCellValue(camera.name)}</span>
        </div>
      </td>
      <td><code className="small">{renderCellValue(camera.camera_number)}</code></td>
      <td>{renderCellValue(camera.location)}</td>
      <td>{renderCellValue(camera.department_name)}</td>
      <td>
        <small className="text-muted">
          {camera.latest_image_timestamp ? formatDateTime(camera.latest_image_timestamp) : tc('messages.na')}
        </small>
      </td>
      <td>
        <div className="d-flex gap-2">
          {canEdit && (
            <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none" onClick={() => onEdit(camera)} title={t('editCamera')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
          )}
          <Button
            variant="link"
            size="sm"
            className="text-primary p-1 text-decoration-none"
            onClick={() => onViewDetails(camera.id)}
            disabled={modalLoading}
            title={t('viewDetails')}
          >
            <i className="bi bi-eye"></i> <span className="ms-1">{tc('buttons.view')}</span>
          </Button>
        </div>
      </td>
    </tr>
  );
});

CameraTableRow.displayName = 'CameraTableRow';

const Cameras: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'cameras' });
  const { t: tc } = useTranslation('common');
  const { currentCompany } = useAuth();
  const { canAdd, canEdit } = useRolePermissions();
  
  // Get company timezone with fallback
  const companyTimezone = currentCompany?.timezone || 'UTC';
  
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Sort state
  const [sortField, setSortField] = useState<keyof Camera>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const [selectedCamera, setSelectedCamera] = useState<Camera | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Department and Edge Device data for form dropdowns
  const [departments, setDepartments] = useState<Department[]>([]);
  const [edgeDevices, setEdgeDevices] = useState<EdgeDevice[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [edgeDevicesLoading, setEdgeDevicesLoading] = useState(false);
  
  // Create Camera Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalLoading, setCreateModalLoading] = useState(false);
  const [createModalError, setCreateModalError] = useState<string | null>(null);
  const [createModalSuccess, setCreateModalSuccess] = useState<string | null>(null);
  
  // Edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<CreateCameraRequest>({
    edge_device_id: 0,
    name: '',
    company_id: currentCompany?.company_id || '',
    department_id: 0,
    ip_address: '',
    port: 554,
    username: '',
    password: '',
    status: true,
    camera_number: '',
    location: ''
  });

  const fetchCameras = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      setError(null);
      const response = await cameraAPI.getCameras(currentCompany.company_id);
      
      if (response.success) {
        setCameras(response.cameras || []);
      } else {
        setError(t('fetchFailed'));
      }
    } catch (err: any) {
      console.error('Error fetching cameras:', err);
      setError(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  const fetchCameraDetails = useCallback(async (id: number) => {
    try {
      setModalLoading(true);
      const response = await cameraAPI.getCameraDetails(id);
      
      if (response.success) {
        setSelectedCamera(response.camera);
        setModalOpen(true);
      } else {
        setError(t('detailsFetchFailed'));
      }
    } catch (err: any) {
      console.error('Error fetching camera details:', err);
      setError(t('detailsLoadFailed'));
    } finally {
      setModalLoading(false);
    }
  }, []);

  const loadDepartments = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setDepartmentsLoading(true);
      const response = await departmentAPI.getDepartments(currentCompany.company_id);
      setDepartments(response.departments || []);
    } catch (error: any) {
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  }, [currentCompany]);

  const loadEdgeDevices = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setEdgeDevicesLoading(true);
      const response = await edgeDeviceAPI.getEdgeDevices(currentCompany.company_id);
      // Handle both array response and object response from API
      const devices = Array.isArray(response) ? response : (response.devices || []);
      setEdgeDevices(devices);
    } catch (error: any) {
      console.error('Error fetching edge devices:', error);
    } finally {
      setEdgeDevicesLoading(false);
    }
  }, [currentCompany]);



  useEffect(() => {
    if (currentCompany) {
      fetchCameras();
      loadDepartments();
      loadEdgeDevices();
    } else {
      setCameras([]);
      setDepartments([]);
      setEdgeDevices([]);
    }
  }, [currentCompany, fetchCameras, loadDepartments, loadEdgeDevices]);

  // Update company_id in form when currentCompany changes
  useEffect(() => {
    if (currentCompany) {
      setFormData(prev => ({ ...prev, company_id: currentCompany.company_id }));
    }
  }, [currentCompany]);

  // Sort options
  const sortOptions = [
    { value: 'name', label: t('cameraName') },
    { value: 'camera_number', label: t('cameraNumber') },
    { value: 'location', label: t('location') },
    { value: 'department_name', label: tc('labels.department') },
    { value: 'status', label: tc('labels.status') },
    { value: 'latest_image_timestamp', label: t('lastImageShort') }
  ];

  // Handle sort change
  const handleSortChange = useCallback((field: keyof Camera) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting
  }, [sortField]);

  // Get sort icon for a column
  const getSortIcon = useCallback((field: keyof Camera) => {
    if (sortField !== field) return <SortIcon />;
    return sortDirection === 'asc' ? <SortUpIcon /> : <SortDownIcon />;
  }, [sortField, sortDirection]);

  // Reset page when search term or sort changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, sortField, sortDirection]);

  // Filter cameras based on search term
  const filteredCameras = useMemo(() => {
    if (!searchTerm.trim()) return cameras;

    const searchLower = searchTerm.toLowerCase();
    return cameras.filter((camera) => {
      return (
        camera.name?.toLowerCase().includes(searchLower) ||
        camera.camera_number?.toLowerCase().includes(searchLower) ||
        camera.location?.toLowerCase().includes(searchLower) ||
        camera.department_name?.toLowerCase().includes(searchLower) ||
        (camera.status ? 'active' : 'inactive').includes(searchLower)
      );
    });
  }, [cameras, searchTerm]);

  // Sort filtered cameras
  const sortedCameras = useMemo(() => {
    return [...filteredCameras].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Handle boolean values (status)
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }

      // Handle date values
      if (sortField === 'latest_image_timestamp') {
        aValue = (aValue && typeof aValue === 'string') ? new Date(aValue).getTime() : 0;
        bValue = (bValue && typeof bValue === 'string') ? new Date(bValue).getTime() : 0;
      }

      // Convert to strings for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [filteredCameras, sortField, sortDirection]);

  // Paginate sorted cameras
  const totalPages = Math.ceil(sortedCameras.length / rowsPerPage);
  const paginatedCameras = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return sortedCameras.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedCameras, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Create Camera Modal handlers
  const handleShowCreateModal = useCallback(() => {
    setIsEditMode(false);
    setEditingCamera(null);
    setShowCreateModal(true);
    setCreateModalError(null);
    setCreateModalSuccess(null);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreateModalError(null);
    setCreateModalSuccess(null);
    setIsEditMode(false);
    setEditingCamera(null);
    setFormData({
      edge_device_id: 0,
      name: '',
      company_id: currentCompany?.company_id || '',
      department_id: 0,
      ip_address: '',
      port: 554,
      username: '',
      password: '',
      status: true,
      camera_number: '',
      location: ''
    });
  }, [currentCompany]);

  const handleFormChange = useCallback((field: keyof CreateCameraRequest, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany) return;

    try {
      setCreateModalLoading(true);
      setCreateModalError(null);
      setCreateModalSuccess(null);

      if (isEditMode && editingCamera) {
        // Update existing camera
        const updateData: UpdateCameraRequest = {
          edge_device_id: formData.edge_device_id,
          name: formData.name,
          department_id: formData.department_id,
          ip_address: formData.ip_address,
          port: formData.port,
          username: formData.username,
          password: formData.password,
          status: formData.status,
          location: formData.location
        };
        
        const response = await cameraAPI.updateCamera(
          editingCamera.id, 
          updateData, 
          currentCompany.company_id
        );
        
        setCreateModalSuccess(response.message || t('updatedSuccess'));
      } else {
        // Create new camera
        const response = await cameraAPI.createCamera(formData);
        setCreateModalSuccess(response.message || t('createdSuccess'));
      }
      
      // Refresh the list
      await fetchCameras();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseCreateModal();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error saving camera:', error);
      
      // Handle API errors
      if (error.response?.data?.message) {
        setCreateModalError(error.response.data.message);
      } else {
        setCreateModalError(error.message || t('saveFailed'));
      }
    } finally {
      setCreateModalLoading(false);
    }
  }, [formData, currentCompany, fetchCameras, handleCloseCreateModal, isEditMode, editingCamera]);

  const handleViewDetails = useCallback((id: number) => {
    fetchCameraDetails(id);
  }, [fetchCameraDetails]);

  const handleEditCamera = useCallback((camera: Camera) => {
    setIsEditMode(true);
    setEditingCamera(camera);
    setShowCreateModal(true);
    setCreateModalError(null);
    setCreateModalSuccess(null);
    setFormData({
      edge_device_id: camera.edge_device_id || 0,
      name: camera.name || '',
      company_id: camera.company_id || currentCompany?.company_id || '',
      department_id: camera.department_id || 0,
      ip_address: camera.ip_address || '',
      port: camera.port || 554,
      username: camera.username || '',
      password: camera.password || '',
      status: camera.status,
      camera_number: camera.camera_number || '',
      location: camera.location || ''
    });
  }, [currentCompany]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedCamera(null);
  }, []);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInCompanyTimezone(dateString, currentCompany?.timezone || 'UTC');
  }, [currentCompany?.timezone]);

  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when company changes
  useEffect(() => {
    if (currentCompany) {
      clearCache(`cameras_${currentCompany.company_id}`);
      setSearchTerm('');
      setPage(1);
      setSortField('name');
      setSortDirection('asc');
    }
  }, [currentCompany]);

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        <div className="page-header">
          <h2 className="an-page-title">
            {t('title')}
          </h2>
          {currentCompany && canAdd('cameras') && (
            <Button variant="primary" onClick={handleShowCreateModal}>
              <i className="bi bi-plus-lg"></i>
              {t('addCamera')}
            </Button>
          )}
        </div>
        
        {currentCompany && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
          </Alert>
        )}

        {!currentCompany && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectCompany', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search and Sort Controls */}
        {currentCompany && (
          <Card className="mb-4 an-data-card">
            <Card.Body className="an-data-card">
              <Row className="g-3">
                <Col md={8}>
                  <InputGroup>
                    <InputGroup.Text>
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={t('searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                    {searchTerm && (
                      <Button variant="outline-secondary" onClick={clearSearch}>
                        <i className="bi bi-x-lg"></i>
                      </Button>
                    )}
                  </InputGroup>
                  {searchTerm && (
                    <small className="text-muted mt-2 d-block">
                      {t('found', { count: filteredCameras.length, term: searchTerm })}
                    </small>
                  )}
                </Col>
                <Col md={4}>
                  <div className="d-flex gap-2">
                    <Form.Select
                      size="sm"
                      value={sortField}
                      onChange={(e) => handleSortChange(e.target.value as keyof Camera)}
                      style={{ minWidth: '150px' }}
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleSortChange(sortField)}
                      title={`${sortOptions.find(opt => opt.value === sortField)?.label} (${sortDirection === 'asc' ? t('ascending') : t('descending')})`}
                    >
                      {getSortIcon(sortField)}
                    </Button>
                  </div>
                  <small className="text-muted mt-1 d-block">
                    {t('sortedBy', { field: sortOptions.find(opt => opt.value === sortField)?.label, direction: sortDirection === 'asc' ? t('asc') : t('desc') })}
                  </small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="p-0 an-data-card">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedCameras.length > 0 ? (
                  paginatedCameras.map((camera) => (
                    <CameraCard
                      key={camera.id}
                      camera={camera}
                      onViewDetails={handleViewDetails}
                      onEdit={handleEditCamera}
                      modalLoading={modalLoading}
                      canEdit={canEdit('cameras')}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <CameraIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                        : t('noneConfigured', { company: currentCompany.description })
                      }
                    </p>
                    {!searchTerm && canAdd('cameras') && (
                      <Button variant="primary" onClick={handleShowCreateModal}>
                        <i className="bi bi-plus-lg"></i>
                        {t('addFirstCamera')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive an-data-table an-panel">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('status')}
                          title={t('sortByStatus')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {tc('labels.status')}
                            {getSortIcon('status')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('name')}
                          title={t('sortByCameraName')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('cameraName')}
                            {getSortIcon('name')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('camera_number')}
                          title={t('sortByCameraNumber')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('cameraNumber')}
                            {getSortIcon('camera_number')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('location')}
                          title={t('sortByLocation')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('location')}
                            {getSortIcon('location')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('department_name')}
                          title={t('sortByDepartment')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {tc('labels.department')}
                            {getSortIcon('department_name')}
                          </div>
                        </th>
                        <th 
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSortChange('latest_image_timestamp')}
                          title={t('sortByLastImage')}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            {t('lastImage')}
                            {getSortIcon('latest_image_timestamp')}
                          </div>
                        </th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCameras.length > 0 ? (
                        paginatedCameras.map((camera) => (
                          <CameraTableRow
                            key={camera.id}
                            camera={camera}
                            onViewDetails={handleViewDetails}
                            onEdit={handleEditCamera}
                            modalLoading={modalLoading}
                            companyTimezone={companyTimezone}
                            canEdit={canEdit('cameras')}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="text-center py-5">
                            <CameraIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                                : t('noneConfigured', { company: currentCompany.description })
                              }
                            </p>
                            {!searchTerm && canAdd('cameras') && (
                              <Button variant="primary" onClick={handleShowCreateModal}>
                                <i className="bi bi-plus-lg"></i>
                                {t('addFirstCamera')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {sortedCameras.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, sortedCameras.length), total: sortedCameras.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0 an-pagination">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}

              {/* Items per page selector */}
              <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-center align-items-center gap-2">
                  <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="text-center py-5 an-data-card">
              <h5>{tc('messages.selectCompanyTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectCompanyBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create/Edit Camera Modal */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditMode ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {createModalSuccess && (
                <Alert variant="success" className="mb-3">
                  {createModalSuccess}
                </Alert>
              )}
              
              {createModalError && (
                <Alert variant="danger" className="mb-3">
                  {createModalError}
                </Alert>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('numberLabel')} {!isEditMode ? '*' : ''}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.camera_number}
                      onChange={(e) => handleFormChange('camera_number', e.target.value)}
                      placeholder={t('numberPlaceholder')}
                      required={!isEditMode}
                      readOnly={isEditMode}
                      disabled={isEditMode}
                      className="bg-light"
                    />
                    {isEditMode && (
                      <Form.Text className="text-muted">
                        {t('numberReadonlyHint')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('locationLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleFormChange('location', e.target.value)}
                      placeholder={t('locationPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('companyLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={currentCompany ? `${currentCompany.description} (${currentCompany.company_id})` : ''}
                      readOnly
                      disabled
                      className="bg-light"
                    />
                    <Form.Text className="text-muted">
                      {isEditMode 
                        ? t('companyEditHint')
                        : t('companyCreateHint')
                      }
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('edgeDeviceLabel')}</Form.Label>
                    <Form.Select
                      value={formData.edge_device_id}
                      onChange={(e) => handleFormChange('edge_device_id', parseInt(e.target.value))}
                      required
                      disabled={edgeDevicesLoading}
                    >
                      <option value={0}>
                        {edgeDevicesLoading ? t('loadingEdgeDevices') : t('selectEdgeDevice')}
                      </option>
                      {edgeDevices.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name}
                        </option>
                      ))}
                    </Form.Select>
                    {edgeDevices.length === 0 && !edgeDevicesLoading && (
                      <Form.Text className="text-warning">
                        {t('noEdgeDevices')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('departmentLabel')}</Form.Label>
                    <Form.Select
                      value={formData.department_id}
                      onChange={(e) => handleFormChange('department_id', parseInt(e.target.value))}
                      required
                      disabled={departmentsLoading}
                    >
                      <option value={0}>
                        {departmentsLoading ? t('loadingDepartments') : t('selectDepartment')}
                      </option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </Form.Select>
                    {departments.length === 0 && !departmentsLoading && (
                      <Form.Text className="text-warning">
                        {t('noDepartments')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('ipLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.ip_address}
                      onChange={(e) => handleFormChange('ip_address', e.target.value)}
                      placeholder={t('ipPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('portLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.port}
                      onChange={(e) => handleFormChange('port', parseInt(e.target.value))}
                      placeholder={t('portPlaceholder')}
                      min="1"
                      max="65535"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('usernameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      placeholder={t('usernamePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('passwordLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      placeholder={t('passwordPlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="camera-status"
                      label={t('cameraActive')}
                      checked={formData.status}
                      onChange={(e) => handleFormChange('status', e.target.checked)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseCreateModal} disabled={createModalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={createModalLoading || formData.edge_device_id === 0 || formData.department_id === 0}
              >
                {createModalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? tc('loading.updating') : tc('loading.creating')}
                  </>
                ) : (
                  <>
                    {isEditMode ? <i className="bi bi-pencil"></i> : <i className="bi bi-plus-lg"></i>}
                    {isEditMode ? t('updateCamera') : t('createCamera')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Bootstrap Camera Details Modal */}
        <Modal 
          show={modalOpen} 
          onHide={handleCloseModal}
          size="lg"
          fullscreen="lg-down"
          scrollable
        >
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title className="d-flex align-items-center">
              {t('detailsTitle')}
            </Modal.Title>
          </Modal.Header>
          
          <Modal.Body className="p-0">
            {modalLoading ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-5">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <span>{t('loadingDetails')}</span>
              </div>
            ) : selectedCamera ? (
              <Container fluid className="an-enterprise-page an-page-shell an-data-page">
                {/* Latest Image Section */}
                {selectedCamera.latest_image_url && (
                  <Card className="mb-4 an-data-card">
                    <Card.Header className="bg-success text-white an-data-card">
                      <h6 className="mb-0">
                        <CameraIcon />
                        {t('lastImage')}
                      </h6>
                    </Card.Header>
                    <Card.Body className="text-center an-data-card">
                      <div className="mb-3">
                        <small className="text-muted text-uppercase fw-bold d-block">{t('timestamp')}</small>
                        <div className="fw-medium">
                          {selectedCamera.latest_image_timestamp ? formatDateTime(selectedCamera.latest_image_timestamp) : 'N/A'}
                        </div>
                      </div>
                      <div className="border rounded p-3 bg-light">
                        <img 
                          src={selectedCamera.latest_image_url} 
                          alt={t('latestCapture')} 
                          className="img-fluid rounded shadow"
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const nextElement = e.currentTarget.nextElementSibling as HTMLDivElement;
                            if (nextElement) nextElement.style.display = 'block';
                          }}
                        />
                        <div style={{ display: 'none' }} className="text-muted p-4">
                          <CameraIcon />
                          <div className="mt-2">{t('unableLoadImage')}</div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Basic Information */}
                <Card className="mb-4 an-data-card">
                  <Card.Header className="bg-light an-data-card">
                    <h6 className="mb-0 text-primary">
                      <CameraIcon />
                      {t('basicInfo')}
                    </h6>
                  </Card.Header>
                  <Card.Body className="an-data-card">
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="border-bottom pb-2 mb-3">
                          <small className="text-muted text-uppercase fw-bold">{t('cameraName')}</small>
                          <div className="fw-medium">{renderCellValue(selectedCamera.name)}</div>
                        </div>
                        <div className="border-bottom pb-2 mb-3">
                          <small className="text-muted text-uppercase fw-bold">{t('cameraNumber')}</small>
                          <div className="fw-medium">
                            <code>{renderCellValue(selectedCamera.camera_number)}</code>
                          </div>
                        </div>
                        <div className="d-flex align-items-start">
                          <LocationIcon />
                          <div className="ms-2">
                            <small className="text-muted text-uppercase fw-bold d-block">{t('location')}</small>
                            <div>{renderCellValue(selectedCamera.location)}</div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="border-bottom pb-2 mb-3">
                          <small className="text-muted text-uppercase fw-bold">{tc('labels.status')}</small>
                          <div>
                            <Badge bg={selectedCamera.status ? 'success' : 'danger'} className="mt-1">
                              {selectedCamera.status ? tc('status.active') : tc('status.inactive')}
                            </Badge>
                          </div>
                        </div>
                        <div>
                          <small className="text-muted text-uppercase fw-bold d-block">{tc('labels.department')}</small>
                          <div>{renderCellValue(selectedCamera.department_name)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>



                {/* Authentication Information */}
                <Card className="mb-4 an-data-card">
                  <Card.Header className="bg-light an-data-card">
                    <h6 className="mb-0 text-primary">
                      <LockIcon />
                      {t('auth')}
                    </h6>
                  </Card.Header>
                  <Card.Body className="an-data-card">
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="d-flex align-items-start">
                          <PersonIcon />
                          <div className="flex-grow-1 ms-2">
                            <small className="text-muted text-uppercase fw-bold d-block">{t('username')}</small>
                            <div className="bg-light p-2 rounded mt-1">
                              <code>{renderCellValue(selectedCamera.username)}</code>
                            </div>
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="d-flex align-items-start">
                          <LockIcon />
                          <div className="flex-grow-1 ms-2">
                            <small className="text-muted text-uppercase fw-bold d-block">{t('password')}</small>
                            <div className="bg-light p-2 rounded mt-1">
                              <code className="text-break">{renderCellValue(selectedCamera.password)}</code>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>


              </Container>
            ) : null}
          </Modal.Body>
          
          <Modal.Footer className="border-top">
            <Button variant="primary" onClick={handleCloseModal} className="px-4">
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default memo(Cameras); 

